import { useRoute } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';

import { ProjectFormSkeleton } from '@components/Projects/ProjectFormSkeleton';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useProjectFromQuery } from '@hooks/useProjectFromQuery';
import { ProjectsStackScreenProps } from '@navigation/projects/projects-stack';

import { FormValues, ProjectFormStack } from './ProjectFormStack';

const EditProject: React.FC = () => {
  const {
    params: { projectUUId },
  } = useRoute<ProjectsStackScreenProps<'project-edit'>['route']>();

  const [projectId, setProjectId] = useState<string>();

  const { project } = useProjectFromQuery({ projectUUId });
  // Scoped on "nothing rendered yet" rather than the old `!project || loading`.
  // `loading` also goes true for a background getProject refetch, and unmounting
  // the form then would throw away whatever the user had typed.
  const showSkeleton = useMinimumVisible(!project);

  useEffect(() => {
    setProjectId(project?.id);
  }, [project]);

  // TA-10386: shape-matched form placeholder instead of a centred spinner.
  // `|| !project` only ever extends the 150ms hold, never cuts it short, and
  // is what makes the field derivations below safe.
  if (showSkeleton || !project) return <ProjectFormSkeleton />;

  const initialMembers = project.members.map((m) => {
    return {
      ...m.user,
      role: m.role,
    };
  });

  const initialInvitedMembers = (project.invitations ?? []).map(
    (inv, index) => ({
      id: `invited-${index}-${inv.email}`,
      name: inv.email,
      email: inv.email,
      role: inv.role,
      expiredAt: inv.expiredAt ?? null,
      __typename: 'InvitedUser' as const,
    })
  );

  const initialValues: FormValues = {
    location: project.address
      ? {
          address: project.address,
          latitude: project?.latitude || 0,
          longitude: project?.longitude || 0,
        }
      : null,
    color: project.color,
    icon: project.icon,
    description: project.description || '',
    id: project.id,
    name: project.name || '',
    teamId: project.team?.id || '',
    users: [...initialMembers, ...initialInvitedMembers],
    currentTeam: project.team,
  };

  return (
    <ProjectFormStack projectId={projectId} initialValues={initialValues} />
  );
};

export default EditProject;
